import React from 'react';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type { TextBlock } from '../../store/chatStore';
import { ExtendedMarkdown } from './ExtendedMarkdown';

// remark-breaks renders single newlines as hard line breaks without touching
// fenced code blocks — unlike a naive `replace(/\n/g, '  \n')`, which appends
// trailing spaces inside code fences and breaks Mermaid's whitespace-sensitive
// grammar. Overriding remarkPlugins drops ChatMarkdown's default, so gfm must
// be re-included here.
const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

interface TextContentProps {
  content: TextBlock;
  role: 'user' | 'assistant';
  isLastMessage?: boolean;
  isLastContent?: boolean;
  isLoading?: boolean;
}

/**
 * Parse `<usermessage:authorName> content </usermessage>` tags.
 * These are injected by external channels (Slack, MCP) to attribute messages.
 * The format uses an invalid HTML tag name (colon + spaces), so we handle it
 * as a plain-text pre-processing step rather than relying on rehype.
 */
function parseUserMessageTag(text: string): {
  author: string | null;
  content: string;
} {
  const match = text.match(/^<usermessage:([^>]+)>\s*([\s\S]*?)\s*<\/usermessage>$/i);
  if (match) {
    return { author: match[1].trim(), content: match[2] };
  }
  return { author: null, content: text };
}

export const TextContent: React.FC<TextContentProps> = ({
  content,
  role,
  isLastMessage,
  isLoading,
  isLastContent,
}) => {
  const { author, content: cleanText } =
    role === 'user' ? parseUserMessageTag(content.text) : { author: null, content: content.text };

  return (
    <div
      className={`prose prose-sm max-w-none ${
        role === 'assistant' ? 'prose-orange' : ''
      }`}
    >
      {author && (
        <div className="not-prose mb-1 flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            {author}
          </span>
        </div>
      )}
      <ExtendedMarkdown
        content={cleanText}
        remarkPlugins={REMARK_PLUGINS}
        isLoading={isLoading}
        textAnimation={{
          animate:
            role === 'assistant' && isLastMessage && isLastContent && isLoading,
          duration: 1,
          letterPerDuration: 2,
        }}
      />
    </div>
  );
};
